import { CLICK_IDS } from '@sunflower-games/analytics-events';
import i18n from 'i18next';
import { action, observable, reaction, toJS } from 'mobx';

import {
  SOCKET_DISCONNECT_MULTISESSION_USER,
  SOCKET_METAGAME_RESULT,
  SOCKET_METAGAME_TERMINATED,
} from '../../config/constants';
import type {
  MetagameFeatureBalance,
  MetagamesUpcomingEvent,
  MetagamesCurrentEvent,
  MetagamesResponse,
  MetagameTokenPackageResponse,
  MetagamesHistoricalEvents,
  MetagameResultSocketData,
  MetagamesUserEvent,
} from '../../dto/metagames.types';
import { MetaGamesEventType } from '../../dto/metagames.types';
import { getUserLanguage } from '../../i18n/helpers';
import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import { getUserStats, ldClient, resolveFlag } from '../../services/launchDarkly';
import { MetagameTokenService } from '../../services/metagameToken.service';
import { bingoQueryKeys, queryClient } from '../../services/queryClient';
import { metagameSocket as socket } from '../../services/socket';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { MetagamesCurrentBingoEvent } from '../bingo/types';
import type { ExtendedCoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

export class MetagamesStore extends Loadable implements IFeatureManagerStore {
  protected readonly rootStore: RootStore;
  private prevLang = getUserLanguage();

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;

    this.initIframePostMessage();

    const metagamesEnabled = resolveFlag('metagames_enabled', true);
    this.metagamesFlag = metagamesEnabled;

    ldClient?.on('ready', () => {
      this.metagamesFlag = resolveFlag('metagames_enabled', true);
    });

    ldClient?.on('change', (changes) => {
      if ('metagames_enabled' in changes) {
        this.metagamesFlag = resolveFlag('metagames_enabled', true);
      }
    });

    const onBingoResultReceived = (socketData: MetagameResultSocketData) => {
      if (socketData.metagameName === MetaGamesEventType.BINGO) {
        this.rootStore.bingoStore.setSocketBingoResult(socketData);
        void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.historicalEvents() });
        void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.currentEvent() });
        void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.upcomingEvents() });
      }
    };

    reaction(
      () => ({
        isAuthenticationFlowDone: this.rootStore.authStore.isAuthenticationFlowDone,
        authorized: this.rootStore.authStore.authorized,
      }),
      ({ isAuthenticationFlowDone, authorized }) => {
        const isUserAuthorized = isAuthenticationFlowDone && authorized;

        if (isUserAuthorized) {
          // Remove existing listeners before adding to prevent duplicates
          socket.off(SOCKET_METAGAME_RESULT, onBingoResultReceived);
          socket.off(SOCKET_METAGAME_TERMINATED);

          // Connect socket if not already connected
          if (!socket.connected) {
            this.handleSocketConnection();
          }

          // Add listeners after ensuring connection
          socket.on(SOCKET_METAGAME_RESULT, onBingoResultReceived);
          socket.on(SOCKET_METAGAME_TERMINATED, () => {
            void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.all });
          });
        } else {
          socket.off(SOCKET_METAGAME_RESULT, onBingoResultReceived);
          socket.off(SOCKET_METAGAME_TERMINATED);
          socket.disconnect();
        }
      },
    );

    socket.on(SOCKET_DISCONNECT_MULTISESSION_USER, () => {
      socket.disconnect();
    });

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        void this.updateMetaGameEvents();
      }
    });
  }

  private handleSocketConnection() {
    const preventMultisessionFlag = resolveFlag('prevent_multisession_enable', false);
    if (preventMultisessionFlag) {
      const LAST_LOGIN_TAB_ID = 'lastLoginTabId';
      const lastLoginTabId = localStorage.getItem(LAST_LOGIN_TAB_ID);
      const tabId = this.rootStore.authStore.tabId;
      if (lastLoginTabId === tabId) {
        socket.connect();
      } else {
        socket.disconnect();
      }
    } else {
      socket.connect();
    }
  }

  @observable
  public onMetagameDialogClose: VoidFunction | undefined = undefined;

  @action
  public setOnMetagameDialogClose(callback: VoidFunction | undefined) {
    this.onMetagameDialogClose = callback;
  }

  private initIframePostMessage() {
    window.addEventListener('message', (e) => {
      const message = e.data;
      const metaGameType = message?.payload?.payload?.gameType;
      // Open metagame event from CrownSlot
      if (message?.type === 'open_metagame' && metaGameType) {
        switch (metaGameType) {
          case 'hammers': {
            void this.rootStore.hammersStore.startHammersGame();
            break;
          }
          case 'puzzle': {
            void this.rootStore.puzzleStore.startPuzzleGame();
            break;
          }
          case 'trail': {
            void this.rootStore.trailStore.startTrailGame();
            break;
          }
          case 'plinko': {
            void this.rootStore.plinkoStore.startPlinkoGame();
            break;
          }
          default: {
            break;
          }
        }
        // Trigger callback on unmount of the dialog
        this.setOnMetagameDialogClose(() => {
          // When opening the metagame dialog from CrownSlot
          // we set a callback to send "metagame_closed"
          // to game ui that will pass it to CrownSlot to refetch the events
          e?.source?.postMessage({ type: 'metagame_closed' }, { targetOrigin: e.origin });
          // Clear callback
          this.setOnMetagameDialogClose(undefined);
        });
      }
    });
  }

  public currentOpenMetagameEvent: MetagamesCurrentEvent | undefined = undefined;

  public setCurrentOpenMetagameEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenMetagameEvent = event;
  }

  public iframeRef: HTMLIFrameElement | null = null;

  public setIframeRef = (iframe: HTMLIFrameElement | null) => {
    this.iframeRef = iframe;
  };

  public dialogTypes: FeatureManagerDialogType[] = [
    'PrizeDrop',
    'PrizeDropPreMessage',
    'Hammers',
    'WelcomeHummersMetagame',
    'PuzzlePreMessage',
    'Trail',
    'TrailPreMessage',
    'RoulettePreMessage',
    'Roulette',
  ];

  @observable
  private metagamesFlag: boolean = false;

  @action
  public setMetagamesFlag(flag: boolean) {
    this.metagamesFlag = flag;
  }

  private SETTER_BY_TYPE: Record<MetaGamesEventType, (event?: MetagamesUserEvent) => void> = {
    [MetaGamesEventType.PRIZE_DROP]: (event) => {
      if (this.rootStore.prizeDropStore.prizeDropFlag) {
        this.rootStore.prizeDropStore.updatePrizeDropEvent(event);
      }
    },
    [MetaGamesEventType.HAMMERS]: (event) => {
      if (this.rootStore.hammersStore.hammersFlag) {
        this.rootStore.hammersStore.setHammersEvent(event);
      }
    },
    [MetaGamesEventType.PUZZLE]: (event) => {
      if (this.rootStore.puzzleStore.puzzleFlag) {
        this.rootStore.puzzleStore.setPuzzleEvent(event);
      }
    },
    [MetaGamesEventType.TRAIL]: (event) => {
      if (this.rootStore.trailStore.trailFlag) {
        this.rootStore.trailStore.setTrailEvent(event);
      }
    },
    [MetaGamesEventType.PLINKO]: (event) => {
      if (event) {
        this.rootStore.plinkoStore.setPlinkoEvent(event);
      }
    },
    [MetaGamesEventType.ROULETTE]: (event) => {
      if (event) {
        this.rootStore.rouletteStore.setRouletteEvent(event);
      }
    },
    [MetaGamesEventType.BINGO]: (event) => {
      if (!this.rootStore.bingoStore.bingoEnabled) {
        return;
      }

      const bingoEvent = event as MetagamesCurrentBingoEvent | undefined;
      this.rootStore.bingoStore.setCurrentMetagameBingoEvent(bingoEvent);

      const convertedBingoEvent = bingoEvent
        ? this.rootStore.bingoStore.convertMetagameEventToBingoEvent(bingoEvent)
        : undefined;
      this.rootStore.bingoStore.setCurrentBingoEvent(convertedBingoEvent);
    },
  };

  public async updateMetaGameEvents() {
    if (!this.metagamesFlag) return 'OFF';

    this.setLoading(true);

    try {
      const response = await getRequest<{ data: MetagamesResponse }>('metagame-events');

      const { userEvents: metagamesEvents, featureBalances } = response.data.metagameEvents;
      const rawFreeDaub = featureBalances?.find((fb: MetagameFeatureBalance) => fb.key === 'bingo_free_daub');
      const freeDaubBalance: MetagameFeatureBalance | null = rawFreeDaub
        ? {
            key: rawFreeDaub.key,
            gcBalance: rawFreeDaub.gcBalance,
            cashBalance: rawFreeDaub.cashBalance,
          }
        : null;
      this.rootStore.bingoStore.setFreeDaubBalance(freeDaubBalance);

      const eventByType = metagamesEvents.reduce<Partial<Record<MetaGamesEventType, MetagamesUserEvent>>>(
        (digest, event) => {
          digest[event.typeName] = event;
          return digest;
        },
        {},
      );

      Object.values(MetaGamesEventType).forEach((eventType) => {
        this.SETTER_BY_TYPE[eventType](eventByType[eventType]);
      });

      return 'READY';
    } catch (error) {
      devError(':MetagamesStore: Error fetching metagames events', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  }

  public async fetch() {
    if (this.loading) return 'FETCHING';

    return this.updateMetaGameEvents();
  }

  startDialogs(type?: FeatureManagerDialogType | undefined) {
    if (type && ['PrizeDrop', 'PrizeDropPreMessage'].includes(type)) {
      return this.rootStore.prizeDropStore.startDialogs(type);
    }

    if (type && ['Hammers', 'WelcomeHummersMetagame'].includes(type)) {
      return this.rootStore.hammersStore.startDialogs(type);
    }

    if (type && ['PuzzlePreMessage'].includes(type)) {
      return this.rootStore.puzzleStore.startDialogs(type);
    }

    if (type && ['Trail', 'TrailPreMessage'].includes(type)) {
      return this.rootStore.trailStore.startDialogs(type);
    }

    if (type && ['RoulettePreMessage', 'Roulette'].includes(type)) {
      return this.rootStore.rouletteStore.startDialogs(type);
    }

    return false;
  }

  public clearIframeRef() {
    this.setIframeRef(null);
  }

  public handleMetagameIframeLoad(iframeRef: HTMLIFrameElement, gameType: MetaGamesEventType) {
    if (!iframeRef.contentWindow) {
      devError('MetagamesStore handleMetagamesIframeLoad: Iframe content window is not available');
      return;
    }

    this.setIframeRef(iframeRef);

    if (this.currentOpenMetagameEvent) {
      this.sendMetagamesGameUiMessage({
        type: 'newMetagamesEvent',
        payload: {
          metagamesEvent: JSON.parse(JSON.stringify(toJS(this.currentOpenMetagameEvent))),
          gameType,
        },
      });

      if (this.rootStore.userStore.profile) {
        this.sendMetagamesGameUiMessage({
          type: 'userReady',
          payload: {
            userProfile: getUserStats(this.rootStore.userStore.profile?.stats),
            language: this.rootStore.userStore.profile.language,
          },
        });
      }
    }
  }

  sendMetagamesGameUiMessage({ type, payload }: { type: string; payload: any }) {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      devError('MetagamesStore sendMetagamesGameUiMessage: Iframe content window is not available');
      return;
    }

    this.iframeRef.contentWindow.postMessage(
      {
        type,
        payload,
      },
      import.meta.env.VITE_GAME_UI_URL,
    );
  }

  public async optInToEvent(eventId: string, coinType: ExtendedCoinType): Promise<void> {
    try {
      await postRequest<{ data: MetagamesResponse }>('metagame-events/opt-in', {
        coinType,
        eventId,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`:MetagamesStore: optInToEvent error: ${errorMessage}`);
      throw error;
    }
  }

  public sendAnalyticsEvent({
    event_action,
    triggered_by,
    feature_sub_type,
    click_location = 'widget',
  }: {
    event_action: 'click' | 'impression';
    triggered_by?: 'click' | 'lobby';
    feature_sub_type: 'prizeDrop' | 'hammers' | 'puzzle' | 'trail' | 'plinko' | 'roulette';
    click_location: 'widget' | 'in_game_widget' | 'close';
  }) {
    const { coinType } = this.rootStore.coinTypeStore;
    const lobby_type = coinType === 'GC' ? 'GC' : 'CC';

    if (event_action === 'click') {
      void analytics.sendEvent('widget', {
        feature_type: 'metagames',
        feature_sub_type: feature_sub_type as any,
        event_action,
        lobby_type,
        click_id: CLICK_IDS.LOBBY_PAGE.WIDGET.METAGAMES,
        click_location,
      });
    } else if (event_action === 'impression') {
      void analytics.sendEvent('widget', {
        feature_type: 'metagames',
        feature_sub_type: feature_sub_type as any,
        event_action,
        triggered_by: triggered_by || 'click',
        lobby_type,
      });
    }
  }

  /**
   * Gets the price and package details for purchasing metagame tokens
   * @param eventType - The type of metagame event (hammers, prizeDrop, puzzle, trail)
   * @param quantity - The number of tokens to purchase
   * @returns Promise with package details including price, currency, and bundle information
   */
  public async getTokenPackagePrice(
    eventType: MetaGamesEventType,
    quantity: number,
  ): Promise<MetagameTokenPackageResponse> {
    try {
      return await MetagameTokenService.getTokenPackagePrice(quantity, eventType);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`:MetagamesStore: Error fetching token package price: ${errorMessage}`);
      throw error;
    }
  }

  public async fetchUpcomingEvents<T>(
    eventType: MetaGamesEventType,
    daysAhead = 30,
  ): Promise<MetagamesUpcomingEvent<T>[]> {
    try {
      const response = await getRequest<{ data: { upcomingEvents: MetagamesUpcomingEvent<T>[] } }>(
        'metagame-events/upcoming-events',
        { eventType, daysAhead },
      );

      return response.data.upcomingEvents;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`:MetagamesStore: Error fetching upcoming events: ${errorMessage}`);
      throw error;
    }
  }

  public async fetchHistoricalEvents<T>(
    type: MetaGamesEventType,
    lastNDays = 30,
  ): Promise<MetagamesHistoricalEvents<T>[]> {
    try {
      const response = await getRequest<{ data: { historicalEvents: MetagamesHistoricalEvents<T>[] } }>(
        'metagame-events/historical-events',
        { type, lastNDays },
      );

      return response.data.historicalEvents || [];
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`:MetagamesStore: Error fetching historical events: ${errorMessage}`);
      return [];
    }
  }
}
